import FlowComponent from "ips/ac/Component";
import Theming from "sap/ui/core/Theming";
import Util from "ips/ac/util/Util";
import Filter from "sap/ui/model/Filter";
import User from "ips/ac/util/classes/user/User";
import { ClientConfigurationType } from "ips/ac/model/odata/generated/IpsCoreModel";

type UiThemeStyle = {
  sapUiBrand: string;
  sapUiIconColor: string;
  sapUiBackgroundColor: string;
}

/**
 * @namespace ips.ac
 */

const DEFAULT_COCKPIT_APP_BRANDING_COLOR = "#0069b4"

export default class ThemeService {
  private PATH: string = "/showDarkMode";
  private static instance: ThemeService;
  private component: FlowComponent;

  private constructor() {
    this.component = Util.getComponent();
  }

  public static getInstance() {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  public init(component: FlowComponent) {
    this.component = component;
  }

  public async toggleDarkMode(userGuid: string) {
    const isDarkMode = !this.isDarkMode();

    //get odata config
    const oDataModel = Util.getODataModel();
    const userFilter = new Filter({ path: "UserGuid", operator: "EQ", value1: userGuid, caseSensitive: false });
    const systemIdFilter = new Filter({
      path: "ConfigurationId",
      operator: "EQ",
      value1: "SHOW_DARK_MODE",
      caseSensitive: false,
    });
    const configs = await oDataModel
      .bindList("/UserConfigurations")
      .filter([userFilter, systemIdFilter])
      .requestContexts();

    if (configs && configs.length > 0) {
      //update odata config first
      const configId = configs[0].getProperty("Sid");
      const context = oDataModel.bindContext("/UserConfigurations('" + configId + "')");
      const newValue = isDarkMode ? "1" : "0";
      context.getBoundContext().setProperty("Value", newValue);
      await oDataModel.submitBatch(oDataModel.getUpdateGroupId());

      //update ui and reload
      window.location.reload();
    }
  }

  public async applyThemes() {
    this.applyTheme();
    await this.applyThemeStyleByClientConfigurationDetails();
  }

  public applyTheme(): void {
    const isDarkMode = User.getInstance().isConfigurationEnabled("SHOW_DARK_MODE")

    if (isDarkMode) {
      Theming.setTheme("sap_horizon_dark");
      document.documentElement.classList.add("ipsDarkMode");
    } else {
      Theming.setTheme("sap_horizon");
      document.documentElement.classList.remove("ipsDarkMode");
    }

    this.component.getAppModel().setProperty(this.PATH, isDarkMode);
  }

  public async applyThemeStyleByClientConfigurationDetails(color?: string): Promise<void> {
    if (!color) {
      color = User.getInstance().getClient().getConfigurationValue(ClientConfigurationType.COCKPIT_APP_BRANDING_COLOR) ?? DEFAULT_COCKPIT_APP_BRANDING_COLOR;
    }

    const uiThemeStyle: UiThemeStyle = {
      sapUiBrand: color,
      sapUiIconColor: color,
      sapUiBackgroundColor: "#fafafa",
    };

    await this.setClientThemeStyle(uiThemeStyle);
  };

  public async setClientThemeStyle(themeStyle: UiThemeStyle): Promise<void> {
    const sUiThemeStylePath = sap.ui.require.toUrl(
      "ips/ac/css/uiThemeStyle.less",
    );

    const response: Response = await fetch(sUiThemeStylePath);
    if (!response.ok) {
      throw new Error("Theme style could not be loaded");
    }

    let style: string = await response.text();

    const styleEl: HTMLStyleElement = document.createElement("style");
    style = style.replace(/@sapUiBrand/gi, themeStyle.sapUiBrand);
    style = style.replace(/@sapUiBackgroundColor/gi, themeStyle.sapUiBackgroundColor);
    style = style.replace(/@sapUiIconColor/gi, themeStyle.sapUiIconColor);

    styleEl.id = "uiThemeStyle";
    styleEl.textContent = style;

    document.head.appendChild(styleEl);
  }

  public isDarkMode(): boolean {
    return Util.getComponent().getAppModel().getProperty(this.PATH);
  }
}
